import React from 'react';
import FilterSection from '../FilterSection';
import FilterDropdown from '../FilterDropdown';
import { Badge } from "@/components/ui/badge";
import { X, Users, Package, Layers, Search, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";

interface CompaniesFilterProps {
  selectedCategories: string[];
  selectedEmployees: string[];
  selectedProductCount: string[];
  selectedModalities: string[];
  selectedDiscounts: string[];
  selectedAddOns: string[];
  slugFilter: string;
  uniqueCategories: string[];
  uniqueEmployees: string[];
  uniqueModalities: string[];
  discountTypes: string[];
  formatDiscountType: (discountType: string) => string;
  handleCategoryChange: (value: string | string[]) => void;
  handleEmployeesChange: (value: string | string[]) => void;
  handleProductCountChange: (value: string | string[]) => void;
  handleModalityChange: (value: string | string[]) => void;
  handleDiscountChange: (value: string | string[]) => void;
  handleAddOnsChange: (value: string | string[]) => void;
  handleSlugFilterChange: (value: string) => void;
  handleRemoveFilter: (type: 'category' | 'employees' | 'productCount' | 'modality' | 'discount' | 'addons' | 'slug', valueToRemove?: string) => void;
}

export function CompaniesFilter({
  selectedCategories,
  selectedEmployees,
  selectedProductCount,
  selectedModalities,
  selectedDiscounts,
  selectedAddOns,
  slugFilter,
  uniqueCategories,
  uniqueEmployees,
  uniqueModalities,
  discountTypes,
  formatDiscountType,
  handleCategoryChange,
  handleEmployeesChange,
  handleProductCountChange,
  handleModalityChange,
  handleDiscountChange,
  handleAddOnsChange,
  handleSlugFilterChange,
  handleRemoveFilter
}: CompaniesFilterProps) {
  
  return (
    <div className="space-y-4">
      {/* Search input on its own line */}
      <div className="mb-4">
        <div className="relative w-full max-w-md">
          <Input
            type="text"
            placeholder="Search by company name"
            value={slugFilter}
            onChange={(e) => handleSlugFilterChange(e.target.value)}
            className="pl-9 h-9 w-full"
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          {slugFilter && (
            <button 
              onClick={() => handleRemoveFilter('slug')}
              className="absolute right-3 top-2.5"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>
      
      <div className="flex flex-wrap items-start gap-2">
        <FilterDropdown
          label="Category"
          options={uniqueCategories}
          value={selectedCategories}
          onChange={handleCategoryChange}
          isMulti={true}
          displayAllText="All Categories"
        />
        
        <FilterDropdown
          label="Employees"
          options={uniqueEmployees}
          value={selectedEmployees}
          onChange={handleEmployeesChange}
          isMulti={true}
          displayAllText="All Employees"
        />
        
        <FilterDropdown
          label="Products"
          options={['All', 'Multiple Products', 'Single Product']}
          value={selectedProductCount}
          onChange={handleProductCountChange}
          isMulti={true}
          displayAllText="All Products"
        />
        

        <FilterDropdown
          label="Modality"
          options={uniqueModalities}
          value={selectedModalities}
          onChange={handleModalityChange}
          isMulti={true}
          displayAllText="All Modalities"
        />

        <FilterDropdown
          label="Discounts"
          options={discountTypes}
          value={selectedDiscounts}
          onChange={handleDiscountChange}
          isMulti={true}
          displayAllText="All Discounts"
          formatOptionLabel={formatDiscountType}
        />

        <FilterDropdown
          label="Add-ons"
          options={['All', 'Has Add-ons', 'No Add-ons']}
          value={selectedAddOns}
          onChange={handleAddOnsChange}
          isMulti={true}
          displayAllText="All Add-ons"
        />
        
      </div>
      
      <div className="flex flex-wrap gap-2">
        {slugFilter && (
          <Badge
            key="slug-filter"
            variant="secondary"
            className="flex items-center gap-1 px-3 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 border-0"
          >
            <Search className="w-3 h-3" />
            Search: {slugFilter}
            <X
              className="h-3 w-3 cursor-pointer"
              onClick={() => handleRemoveFilter('slug')}
            />
          </Badge>
        )}
        {selectedCategories.map(category =>
          category !== 'All' && (
            <Badge
              key={`category-${category}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-[#D3E4FD] hover:bg-[#C2D9F9] text-blue-700 border-0"
            >
              {category}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveFilter('category', category)}
              />
            </Badge>
          )
        )}
        {selectedEmployees.map(employee =>
          employee !== 'All' && (
            <Badge
              key={`employee-${employee}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-gray-50 hover:bg-gray-100 text-gray-700 border-0"
            >
              <Users className="w-3 h-3" />
              {employee}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveFilter('employees', employee)}
              />
            </Badge>
          )
        )}
        {selectedProductCount.map(count =>
          count !== 'All' && (
            <Badge
              key={`product-count-${count}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-transparent hover:bg-gray-50 text-gray-700 border border-gray-300"
            >
              <Package className="w-3 h-3" />
              {count}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveFilter('productCount', count)}
              />
            </Badge>
          )
        )}

        {selectedModalities.map(modality =>
          modality !== 'All' && (
            <Badge
              key={`modality-${modality}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 border-0"
            >
              <Layers className="w-3 h-3" />
              {modality}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveFilter('modality', modality)}
              />
            </Badge>
          )
        )}
        {selectedDiscounts.map(discount =>
          discount !== 'All' && (
            <Badge
              key={`discount-${discount}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-green-50 hover:bg-green-100 text-green-700 border-0"
            >
              <Tag className="w-3 h-3" />
              {formatDiscountType(discount)}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveFilter('discount', discount)}
              />
            </Badge>
          )
        )}
        {selectedAddOns.map(addon =>
          addon !== 'All' && (
            <Badge
              key={`addon-${addon}`}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-0"
            >
              <Package className="w-3 h-3" />
              {addon}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveFilter('addons', addon)}
              />
            </Badge>
          )
        )}
        
      </div>
    </div>
  );
}
